import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent } from '@angular/material/legacy-paginator';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { isString } from 'lodash';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { dateConfig, debounceConfig } from '@app/core/_config/';
import { EInputType } from '@app/core/_constant/input-type.constants';
import { ProductStatus } from '@app/core/_constant/product.constants';
import { AuthService } from '@app/core/auth';
import { PoService } from '@app/core/purchase-orders/services/po.service';
import { ReceivingReportService } from '@app/core/purchase-orders/services/receiving-report.service';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { UIStateService } from '@app/shared/services/ui-state.service';
import { ReceivingReportUnifiedFormComponent } from 'v2/modules/receiving-report/components/receiving-report-unified-form/receiving-report-unified-form.component';
import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';
import { SupplierApiService } from 'v2/modules/shared/services/api/supplier/supplier-api.service';
import { ISupplierSku } from 'v2/modules/supplier/interfaces/supplier-sku.interface';

import { PrintLabelComponent } from '../print-label/print-label.component';

interface _IReceivingReportFilter {
  date ?:string;
  dateTo ?:string;
  ids ?:string;
  poNumber ?:string;
  supplierId ?:string;
}
const _FILTER_KEYS :ReadonlyArray<string> = [
  'date',
  'dateTo',
  'ids',
  'poNumber',
  'supplierId',
] as const satisfies readonly (keyof _IReceivingReportFilter)[];

interface _IReceivingReportParams {
  all ?:1;
  page :number;
  per_page :number;
  sort :string;
  sort_direction :string;
}

type _IReceivingReportRequestParams = _IReceivingReportParams &
  Omit<_IReceivingReportFilter, 'poNumber'> & {
    poNumber ?:string | number;
  };

@Component({
  selector: 'cnc-receiving-reports',
  templateUrl: './receiving-reports.component.html',
  styleUrls: ['./receiving-reports.component.scss'],
})
export class ReceivingReportsComponent implements OnInit, OnDestroy {
  @Input() add$ :Observable<void>;

  #onDestroy$ = new Subject<void>();

  url :string = environment.url;

  ordersRecord :any = { data: [] };
  pageSizeOptions :number[] = [5, 10, 25, 100];
  minDate :string = dateConfig.limit.min;
  maxDate :string = dateConfig.limit.max;
  debounceTime :number = debounceConfig.time;

  productStatus = ProductStatus;

  filter :_IReceivingReportFilter = {};
  params :_IReceivingReportParams = {
    page: 1,
    per_page: 100,
    sort: 'received_date',
    sort_direction: 'desc',
  };

  poSubscription :Subscription;

  COMPANY_LOGO_KEY :string = 'company_logo';
  companyLogo :string = '';
  loading :boolean = false;
  suppliers :ISupplierSku[] = [];

  public filterConfig :DynamicFilterConfig[] = [
    {
      id: 'receiptId',
      query: 'ids',
      type: 'multiInput',
      inputMode: 'numeric',
      label: 'Receipt ID',
      placeholder: 'Enter Receipt ID',
    },
    {
      id: 'date',
      query: 'date',
      type: 'date',
      label: 'Received Date From',
    },
    {
      id: 'dateTo',
      query: 'dateTo',
      type: 'date',
      label: 'Received Date To',
    },
    {
      id: 'poNumber',
      query: 'poNumber',
      type: 'input',
      label: 'Purchase Order No.',
    },
    {
      id: 'supplierId',
      query: 'supplierId',
      type: 'select',
      label: 'Supplier',
      options: [],
    },
  ];

  public defaultFilters = ['receiptId', 'date', 'dateTo', 'poNumber', 'supplierId'];

  readonly EFeatureFlag = EFeatureFlag;

  constructor(
    public receivingReportSvc :ReceivingReportService,
    private cd :ChangeDetectorRef,
    private modalSvc :NgbModal,
    private translate :TranslateService,
    private toastr :ToastrService,
    private poSvc :PoService,
    public authService :AuthService,
    private uiStateSvc :UIStateService,
    private settingsSvc :SettingsService,
    private ft :FeatureToggleService,
    private dialog :MatDialog,
    private supplierApiService :SupplierApiService,
  ) {}

  ngOnInit() {
    this.#initAddHandler();
    this.initFilters();
    this.setFiltersOnInit();
    this.initLogoFromSettings().subscribe();
    this.#getRecords();
    this.poSubscription = this.receivingReportSvc.receivingReports$.subscribe(
      (records) => {
        this.ordersRecord = records;
        this.cd.detectChanges();
      },
    );
    this.settingsSvc.getTrackInventoryByLocationValues();

    if (!this.ft.isActive('utah-UTAH-303-receipt_id_col_filter_and_sort')) {
      this.filterConfig = this.filterConfig.filter(({ id }) => id !== 'receiptId');
      this.defaultFilters = this.defaultFilters.filter((_f) => _f !== 'receiptId');
    }
  }

  ngOnDestroy() {
    this.poSubscription.unsubscribe();
    this.#onDestroy$.next();
    this.#onDestroy$.complete();
  }

  onQueryChange(event) {
    this.filter = event;
    this.params.page = 1;

    this.#getRecords();
  }

  handlePagination(event :LegacyPageEvent) {
    this.params.page = event.pageIndex + 1;
    this.params.per_page = event.pageSize;

    this.#getRecords();
  }

  openDeliveryForm(receivingReport ?:any) {
    this.dialog
      .open(ReceivingReportUnifiedFormComponent, {
        data: { id: receivingReport.id },
        ...DialogConfig.XX_LARGE,
      })
      .afterClosed()
      .pipe(takeUntil(this.#onDestroy$))
      .subscribe((response) => {
        if (response) {
          this.#getRecords();
        }
      });
  }

  deleteReceivingReport(receivingReport :any) {
    Swal.fire({
      title: 'Confirm Delete?',
      text: 'Delete purchase order receiving report?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.receivingReportSvc
          .deleteReceivingReport(receivingReport.id)
          .subscribe(() => {
            this.toastr.success(
              'Successfully deleted purchase order receiving report.',
              'Success',
            );
          });
      }
    });
  }

  exportListToPdf() {
    Swal.fire('Exporting to PDF, please wait...');
    Swal.showLoading();

    const params = this.#buildRequestParams({ all: 1, page: 1 });

    this.receivingReportSvc.getAsync(params).subscribe((resp) => {
      if (resp) {
        resp['companyLogo'] = this.companyLogo;
        this.receivingReportSvc.exportListtoPDF(resp);
      }
    });
  }

  exportListToCsv() {
    Swal.fire('Exporting to CSV, please wait...');
    Swal.showLoading();

    const params = this.#buildRequestParams({ all: 1, page: 1 });

    this.receivingReportSvc.getAsync(params).subscribe((resp) => {
      this.receivingReportSvc.exportListtoCSV(resp);
    });
  }

  resetFilters() {
    this.filter = {};
    for (const key of _FILTER_KEYS) {
      delete this.params[key]; // clear filters from this.params
    }
    delete this.params.all; // ensure `all` param is not present
    this.#getRecords();
  }

  validateDate(prop :string) :void {
    const min = moment(dateConfig.limit.min);
    const max = moment(dateConfig.limit.max);

    if (Object.hasOwn(this.filter, prop)) {
      const inputDate = moment(this.filter[prop]);

      if (inputDate.isValid()) {
        if (inputDate.isBefore(min)) {
          this.filter[prop] = inputDate
            .set('year', min.year())
            .format('YYYY-MM-DD');
        }

        if (inputDate.isAfter(max)) {
          this.filter[prop] = inputDate
            .set('year', max.year())
            .format('YYYY-MM-DD');
        }
      } else {
        this.filter[prop] = min.format('YYYY-MM-DD');
      }

      this.params.page = 1;
      this.#getRecords();
    }
  }

  setFiltersOnInit() :void {
    const stateView = this.uiStateSvc.getState('purchaseOrdersReceivingReports');

    if (stateView) {
      // convert comma-separated IDs back to array if present
      if (stateView.ids && typeof stateView.ids === 'string') {
        stateView.ids = stateView.ids.split(',');
      }

      // split the state back into this.filter and this.params
      Object.entries(stateView).forEach(([key, value]) => {
        if (_FILTER_KEYS.includes(key)) {
          this.filter[key] = value;
        } else {
          this.params[key] = value;
        }
      });
    }
  }

  printLabels(receivingReport ?:any) :void {
    const modalRef = this.modalSvc.open(PrintLabelComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });
    if (receivingReport) {
      modalRef.componentInstance.receivingReport = receivingReport;
    }
  }

  onOpenReceivingReportForm() :void {
    this.dialog
      .open(ReceivingReportUnifiedFormComponent, { ...DialogConfig.XX_LARGE })
      .afterClosed()
      .pipe(takeUntil(this.#onDestroy$))
      .subscribe((response) => {
        if (response) {
          this.#getRecords();
        }
      });
  }

  exportRFQtoPDF(data :any) :void {
    if (data) {
      data['companyLogo'] = this.companyLogo;
      this.receivingReportSvc.exportRFQtoPDF(data);
    }
  }

  setOrder(field :string) :void {
    const defaultDirection = ['received_date'].includes(field) ? 'desc' : 'asc';

    // FIRST CLICK: sort the field in its default direction
    if (this.params.sort !== field) {
      this.params.sort = field;
      this.params.sort_direction = defaultDirection;
    }
    // SECOND CLICK: alternate the direction
    else if (this.params.sort_direction === defaultDirection) {
      this.params.sort_direction = defaultDirection === 'asc' ? 'desc' : 'asc';
    }
    // THIRD CLICK: remove sort entirely
    else {
      this.params.sort = '';
      this.params.sort_direction = '';
    }

    this.params.page = 1;
    this.#getRecords();
  }

  private initLogoFromSettings() :Observable<any> {
    return this.settingsSvc.getKeyValue(this.COMPANY_LOGO_KEY).pipe(
      tap((response) => {
        if (response && isString(response)) {
          this.getLogoKeyFromSettings(response).subscribe();
        }
      }),
      takeUntil(this.#onDestroy$),
    );
  }
  private getLogoKeyFromSettings(filename :string) :Observable<any> {
    return this.settingsSvc.getProfileLogo(filename).pipe(
      tap((response) => {
        if (filename.endsWith('.webp')) {
          this.companyLogo = 'data:image/webp;base64,' + response;
        } else {
          this.companyLogo = 'data:image/png;base64,' + response;
        }
      }),
      takeUntil(this.#onDestroy$),
    );
  }

  private initFilters() :void {
    this.getSuppliers();
  }

  private getSuppliers() :void {
    this.loading = true;
    this.supplierApiService
      .get({})
      .pipe(
        takeUntil(this.#onDestroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response) => {
          const { records } = response;
          this.suppliers = records;
          this.filterConfig.map((config) => {
            if (config.id === 'supplierId' && config.type === EInputType.SELECT) {
              config.options = records.map((record) => ({
                label: record.factory_name,
                value: record.id,
              }));
            }
          });
        },
      });
  }

  /**
   * @param overrides  One-off changes to the request params; they are _not_
   *  saved to `this.params` or `this.filter`.
   * @returns A new request params object.
   */
  #buildRequestParams(
    overrides ?:Partial<_IReceivingReportRequestParams>,
  ) :_IReceivingReportRequestParams {
    const params = {
      ...this.params,
      ...this.filter,
      ...overrides,
    };

    if (params.ids && Array.isArray(params.ids)) {
      params.ids = params.ids.join(',');
    }

    if (params.poNumber && typeof params.poNumber === 'string') {
      params.poNumber = this.poSvc.getPoNumberFromPOString(params.poNumber);
    }

    return params;
  }

  #getRecords() {
    const params :_IReceivingReportRequestParams = this.#buildRequestParams();

    this.uiStateSvc.setState('purchaseOrdersReceivingReports', params);
    this.receivingReportSvc.get(params);
  }

  #initAddHandler() {
    if (this.add$ && this.add$ instanceof Observable) {
      this.add$.pipe(takeUntil(this.#onDestroy$)).subscribe(() => {
        this.onOpenReceivingReportForm();
      });
    }
  }
}
